import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { ColDef, GridApi, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { useHistory } from "react-router-dom";

import { EntityType } from "@mapmycustomers/shared/enum";
import Person from "@mapmycustomers/shared/types/entity/Person";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import ActivityAnnotationLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ActivityAnnotationLinkCellRenderer";
import ColorCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ColorCellRenderer";
import CompaniesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CompaniesCellRenderer";
import CrmLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CrmLinkCellRenderer";
import EmailCellRenderer from "@app/component/grid/gridComponents/cellRenderers/EmailCellRenderer";
import FrequencyCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyCellRenderer";
import FrequencyIntervalCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyIntervalCellRenderer";
import GroupsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/GroupsCellRenderer";
import LocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/LocationCellRenderer";
import MultiOptionCustomFieldCellRenderer from "@app/component/grid/gridComponents/cellRenderers/MultiOptionCustomFieldCellRenderer";
import NameCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NameCellRenderer";
import NotesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NotesCellRenderer";
import PhoneCellRenderer from "@app/component/grid/gridComponents/cellRenderers/PhoneCellRenderer";
import PrimaryCompanyCellRenderer from "@app/component/grid/gridComponents/cellRenderers/PrimaryCompanyCellRenderer";
import RoutesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/RoutesCellRenderer";
import TerritoriesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TerritoriesCellRenderer";
import UserCellRenderer from "@app/component/grid/gridComponents/cellRenderers/UserCellRenderer";
import WebsiteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/WebsiteCellRenderer";
import AgGridListView from "@app/component/view/ListView/AgGridListView";
import AgGridListViewServerSideGrid from "@app/component/view/ListView/AgGridListViewServerSideGrid";
import AgGridListViewPagination from "@app/component/view/ListView/components/Pagination/AgGridListViewPagination";
import SelectBar from "@app/component/view/SelectBar";
import Path from "@app/enum/Path";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import personFieldModel from "@app/util/fieldModel/PersonFieldModel";

import CountryCellRenderer from "../../component/grid/gridComponents/cellRenderers/CountryCellRenderer";
import TeamsCellRenderer from "../../component/grid/gridComponents/cellRenderers/TeamsCellRenderer";

import PeopleListToolbar from "./component/PeopleListToolbar";
import Subheader from "./component/Subheader";
import styles from "./PeopleListPage.module.scss";
import peopleTableAnalytics from "./peopleTableAnalytics";
import { fetchList, initializeListView } from "./store/actions";
import {
  getGridColumnDefs,
  getListViewState,
  getTotalFilteredRecordsCount,
  getTotalRecordsCount,
  isLoading,
} from "./store/selectors";

interface Props {
  columnDefs: ColDef[];
  initialize: () => void;
  listFetcher: typeof fetchList.request;
  listViewState: ViewState;
  loading: boolean;
  subscribe: typeof subscribe;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
}

const PeopleListPage: React.FC<Props> = ({
  columnDefs,
  initialize,
  listFetcher,
  listViewState,
  loading,
  subscribe,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
}) => {
  useEffect(() => {
    initialize();
  }, [initialize]);

  const history = useHistory();
  const handleViewClick = useCallback(
    (person: Person) => {
      history.push(`${Path.PEOPLE}/${person.id}`);
      peopleTableAnalytics.clicked(["View"], { person });
    },
    [history]
  );

  const [selectedEntities, setSelectedEntities] = useState<Set<Person>>(new Set());
  const handleRowSelectionChanged = useDebouncedCallback(
    [({ api }: RowSelectedEvent) => setSelectedEntities(new Set(api.getSelectedRows())), 250],
    [setSelectedEntities]
  );

  const gridApi = useRef<GridApi | undefined>(undefined);
  const handleGridReady = useCallback((event: GridReadyEvent) => (gridApi.current = event.api), []);

  useEffect(() => {
    // reset selected rows when filter changes
    gridApi.current?.deselectAll();
  }, [listViewState.filter]);

  const handleReloadData = useCallback(() => {
    gridApi.current?.deselectAll();
    const sortModel = gridApi.current?.getSortModel();
    if (
      sortModel &&
      sortModel.length &&
      sortModel[0].colId === "updatedAt" &&
      sortModel[0].sort === "desc"
    ) {
      gridApi.current?.onFilterChanged(); // force reload and go to the first page
    } else {
      gridApi.current?.onSortChanged(); // force reload
    }
  }, []);

  useEffect(() => {
    // TODO: use more sophisticated logic in a future, e.g. only reload if deleted company exists in a current view
    const listener = {
      onAdd: () => handleReloadData(),
      onDelete: () => handleReloadData(),
      onUpdate: () => handleReloadData(),
    };
    subscribe({ entityTypes: [EntityType.PERSON], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.PERSON], listener });
    };
  }, [handleReloadData, subscribe, unsubscribe]);

  return (
    <AnalyticsContext.Provider value={peopleTableAnalytics}>
      <AgGridListView<Person>
        columnDefs={columnDefs}
        entityType={EntityType.PERSON}
        fieldModel={personFieldModel}
        listFetcher={listFetcher}
        listViewState={listViewState}
        totalRecords={totalRecords}
      >
        <Subheader totalFilteredRecords={totalFilteredRecords} totalRecords={totalRecords} />
        <section className={styles.listViewGrid}>
          {selectedEntities.size > 0 && (
            <SelectBar
              entityType={EntityType.PERSON}
              onReloadData={handleReloadData}
              selectedEntities={selectedEntities}
              visible
            />
          )}
          <PeopleListToolbar />
          <AgGridListViewServerSideGrid
            context={{
              onView: handleViewClick,
            }}
            enableCellTextSelection // Note: When this is set to true, the clipboard service is disabled.
            frameworkComponents={{
              activityAnnotationLinkCellRenderer: ActivityAnnotationLinkCellRenderer,
              colorCellRenderer: ColorCellRenderer,
              companiesCellRenderer: CompaniesCellRenderer,
              countryCellRenderer: CountryCellRenderer,
              crmLinkCellRenderer: CrmLinkCellRenderer,
              emailCellRenderer: EmailCellRenderer,
              frequencyCellRenderer: FrequencyCellRenderer,
              frequencyIntervalCellRenderer: FrequencyIntervalCellRenderer,
              groupsCellRenderer: GroupsCellRenderer,
              locationCellRenderer: LocationCellRenderer,
              multiOptionCustomFieldCellRenderer: MultiOptionCustomFieldCellRenderer,
              nameCellRenderer: NameCellRenderer,
              notesCellRenderer: NotesCellRenderer,
              phoneCellRenderer: PhoneCellRenderer,
              primaryCompanyCellRenderer: PrimaryCompanyCellRenderer,
              routesCellRenderer: RoutesCellRenderer,
              teamsCellRenderer: TeamsCellRenderer,
              territoriesCellRenderer: TerritoriesCellRenderer,
              userCellRenderer: UserCellRenderer,
              websiteCellRenderer: WebsiteCellRenderer,
            }}
            modules={[MenuModule]}
            onGridReady={handleGridReady}
            onSelectionChanged={handleRowSelectionChanged}
            paginationPageSize={listViewState.range.endRow - listViewState.range.startRow}
            showNoRows={!totalFilteredRecords && !loading}
            suppressPaginationPanel={true}
          />
        </section>
        <AgGridListViewPagination showSizeChanger totalRecords={totalFilteredRecords} />
      </AgGridListView>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  columnDefs: getGridColumnDefs(state)(false),
  listViewState: getListViewState(state),
  loading: isLoading(state),
  totalFilteredRecords: getTotalFilteredRecordsCount(state),
  totalRecords: getTotalRecordsCount(state),
});

const mapDispatchToProps = {
  initialize: initializeListView.request,
  listFetcher: fetchList.request,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(PeopleListPage);
